import { DateTime, Duration } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { formatCurrencyCents, formatDateFromString } from 'shared/utils/formatting';
import { isFreelanceShift, isTempworkShift } from 'shared/services/shift-service-types';
import { hasClaimedState, isShowState } from 'shared/utils/shift-claim-state';
import { daysHoursMinutesUntil } from './utils/days-hours-minutes-until';
import { ShiftInfoTranslator } from './shift-info-button/shift-info-translator';
import { ShiftDetailHeadFlexPools } from './shift-detail-head-flex-pools';

const displayTotalRate = (
  data: ShiftDetail,
  shiftTimeLengthInMs: number,
  shiftBreakLengthInMs: number,
  showTempWorkRate: boolean,
  t: TFunction<'web-common'>
) => {
  let totalRate = '- ';

  if (isFreelanceShift(data)) {
    totalRate = t('shift-detail-head.total-rate.label', {
      amount: formatCurrencyCents(
        ((shiftTimeLengthInMs - shiftBreakLengthInMs) / 1000 / 60 / 60) *
          data.freelanceProperties.hourlyRateCents
      ),
    });
  } else if (isTempworkShift(data) && showTempWorkRate) {
    totalRate = t('shift-detail-head.total-rate.label', {
      amount: formatCurrencyCents(
        ((shiftTimeLengthInMs - shiftBreakLengthInMs) / 1000 / 60 / 60) *
          data.tempWorkProperties.grossHourlyRateCents
      ),
    });
  }

  return totalRate;
};

export const useGetShiftDetailHeadProperties = (
  data: ShiftDetail,
  showTempWorkRate: boolean,
  hideLimitedCompanyShiftFields: boolean
) => {
  const { t } = useTranslation('web-common');

  return useMemo(() => {
    const acceptedClaimantsCount = data.claimants
      .filter(hasClaimedState)
      .filter(isShowState).length;
    const shiftTimeLengthInMs = DateTime.fromISO(data.isoEndDateTime).diff(
      DateTime.fromISO(data.isoStartDateTime)
    ).milliseconds;
    const shiftBreakLengthInMs = data.breakMinutes * 60 * 1000;
    const duration = Duration.fromMillis(shiftTimeLengthInMs).normalize().toFormat('hh:mm');

    const translator = new ShiftInfoTranslator();

    const cooldownDuration = Duration.fromObject({ minutes: data.workCooldownMinutes })
      .normalize()
      .toFormat('hh:mm');

    const certificates = [
      ...new Set(
        [...data.jobTypeCertificates, ...data.shiftCertificates].map(
          (certificate) => certificate.name
        )
      ),
    ];

    const businessUnitInfo = hideLimitedCompanyShiftFields
      ? data.companyName
      : `${data.companyName} / ${data.branchOfficeName}, ${data.departmentName}`;

    return {
      businessUnitInfo,
      jobName: data.name,
      history: translator.translateEvents(data.history),
      seats: t('shift-detail-head.seats.text', {
        acceptedCount: acceptedClaimantsCount,
        maxClaimants: data.maxClaimants,
      }),
      startDate: formatDateFromString(data.startDate),
      endDate: formatDateFromString(data.endDate),
      startTime: data.startTime,
      endTime: data.endTime,
      duration: t('shift-detail-head.duration.text', {
        startTime: data.startTime,
        endTime: data.endTime,
        duration,
      }),
      break:
        data.breakMinutes === 0
          ? t('shift-detail-head.break.none')
          : t('shift-detail-head.break.minutes', { minutes: data.breakMinutes }),
      totalRate: displayTotalRate(
        data,
        shiftTimeLengthInMs,
        shiftBreakLengthInMs,
        showTempWorkRate,
        t
      ),
      cancelationPeriod: daysHoursMinutesUntil(
        data.mutableUntilDate,
        t('shift-detail-head.cancelation-period.expired')
      ),
      cooldownPeriod: t('shift-detail-head.cooldown-period.text', { duration: cooldownDuration }),
      flexPoolNames: <ShiftDetailHeadFlexPools flexPools={data.flexPools} />,
      location: `${data.location.street} ${data.location.houseNumber}`,
      certificates,
      descriptionShort: data.descriptionShort,
      descriptionLong: data.descriptionLong,
      descriptionLawRequirements: data.descriptionLawRequirements,
    };
  }, [data, hideLimitedCompanyShiftFields, showTempWorkRate, t]);
};
